import { useSelector, useDispatch } from "react-redux";
import { useCallback, useState, useEffect, useRef } from "react";
import Modal, { setSelectedModal } from "../../Modal";
import { Formik, Form, Field } from "formik";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import classNames from "classnames";
import { useNavigate } from "react-router";
import {
  EMPTY_ARRAY,
  EMPTY_STRING,
  INDEX,
  MODAL_SIZE,
  REGEX,
  VALIDATION_MESSAGES,
  ZERO,
  DATA_TYPE,
  IS_PRIMARY,
  DATA_TYPE_DESCRIPTIONS,
  SELECTED_CHOICES,
} from "../../../../constants";
import { selectPredefinedCollections } from "../../../../pages/Applications";
import {
  createCollection,
  selectCollection,
  selectCollections,
  selectSelectedDataModelId,
  updateCollection,
} from "../../../../pages/DataModels";
import { crossIcon, deleteIcon } from "../../../../assets/icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { setSelectedDrawer } from "../../Drawer";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { INTERFACES } from "../../AppDrawers/drawers.constants";
import { getInterfaceAndDatatype } from "../../../../helpers";

const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
  SPECIAL_CHARACTERS_WHITESPACE_IN_BETWEEN,
} = REGEX;
const { FIRST, SECOND } = INDEX;
const {
  CHECK_PROPERTY_NAME,
  COLLECTION_REQUIRED,
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_WHITESPACE_IN_BETWEEN,
} = VALIDATION_MESSAGES;

const { XL } = MODAL_SIZE;
const { CREATE_COLLECTION, UPDATE_COLLECTION } = SPINNER_NAMES;
const { DATE_TIME, IMAGE } = INTERFACES;
const PrimaryKeyMessage = "There must be a column as Primary Key";
const FieldsErrorMessage = "Column Name or Data type cannot be Empty";

const TableComponent = ({ rowData = [], setRowData , selectedCollectionTemplate}) => {
  const gridRef = useRef();
  const [isOneToManySelected, setIsOneToManySelected] = useState(false);
  const { data: collections } = useSelector(selectCollections);
  const selectedCollection = useSelector(selectCollection);

  const filteredOptions = collections?.reduce((options, collection) => {
    if (collection.name !== selectedCollection?.name)
      options = [
        ...options,
        {
          name: collection.name,
          value: collection.id,
        },
      ];
    return options;
  }, []);

  const handleReferenceTableChange = (params) => {
    const newValue = params.value;
    const rowIndex = params.node.rowIndex;
    const updatedRowData = [...rowData];
    updatedRowData[rowIndex] = {
      ...updatedRowData[rowIndex],
      referenceTableId: newValue,
    };
    setRowData(updatedRowData);
  };

  const referenceTableCellRenderer = (params) => {
    return (
      <select
        value={params.value}
        onChange={(e) =>
          handleReferenceTableChange({ ...params, value: e.target.value })
        }
        className="w-full h-full text-sm text-black border-none focus:ring-transparent rounded-lg bg-gray-200  cursor-pointer"
      >
        <option value={EMPTY_STRING} hidden>
          Select Table
        </option>
        {filteredOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
    );
  };

  const optionsRenderer = (params) => 
    (
      <select
        value={params.value}
        onChange={(e) =>
          handleDataTypeChange({ ...params, value: e.target.value })
        }
        className="w-full text-sm font-medium text-gray-50 bg-gray-700 focus:border-blue-500 overflow-y-scroll"
      >
        <option value={EMPTY_STRING} hidden>
          Select DataType
        </option>
        {DATA_TYPE_DESCRIPTIONS.map((option) => (
          <option key={option} value={option} >
            {option}
          </option>
        ))}
      </select>
    );

  const handleDataTypeChange = (params) => {
    const newValue = params.value;
    const rowIndex = params.node.rowIndex;
    const updatedRowData = [...rowData];
    updatedRowData[rowIndex] = {
      ...updatedRowData[rowIndex],
      dataType: newValue,
    };
    setIsOneToManySelected(newValue === "One-to-Many");
    setRowData(updatedRowData);
  };

  const booleanCellRenderer = (params) => {
    return (
      <label className="relative inline-flex items-center mb-5 cursor-pointer">
        <input
          type="checkbox"
          checked={params.value || false}
          disabled={ params.colDef.field !== IS_PRIMARY && !!params.data.isPrimary}
          className="sr-only peer  disabled:cursor-not-allowed"
          onChange={(e) => params.setValue(e.target.checked)}
        />
        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
      </label>
    );
  };
  const handleDeleteClick = useCallback(
    (rowNode) => {
      const updatedRowData = rowData.filter((row) => row !== rowNode.data);
      setRowData(updatedRowData);
    },
    [rowData, setRowData]
  );

  const buttonFormatter = (params) => (
    <>
      {!params.node.data.isPrimary && (
        <div
          className="rounded bg-gray-900 w-5 h-5 m-1 flex justify-center items-center"
          onClick={() => handleDeleteClick(params.node)}
        >
          <img src={deleteIcon} alt="delete-icon" />
        </div>
      )}
    </>
  );

  const columnDefs = [
    {
      headerName: "",
      field: "drag",
      editable: false,
      rowDrag: true,
      width: 50,
      maxWidth: 50,
    },
    {
      headerName: "Column Name",
      field: "columnName",
      editable: true,
    },
    {
      headerName: "Data Type",
      field: "dataType",
      editable: true,
      cellEditor: optionsRenderer, 
    },
    {
      headerName: "isRequired",
      field: "isRequired",
      editable: true,
      cellRenderer: booleanCellRenderer,
    },
    {
      headerName: "isPrimary",
      field: "isPrimary",
      editable: true,
      cellRenderer: booleanCellRenderer,
    },
    {
      headerName: "Reference Table",
      field: "referenceTableId",
      editable: true,
      cellRenderer: referenceTableCellRenderer,
      hide: !isOneToManySelected,
    },
    { 
      headerName: "Default Value",
      field: "defaultValue",
      editable: (params) => !params.data.isPrimary,
    },
    {
      headerName: "",
      field: "editdelete",
      editable: false,
      cellRenderer: buttonFormatter,
      width: 80,
      maxWidth: 80,
    },
  ];

  const focusFirstCell = useCallback(() => {
    if (gridRef.current && gridRef.current.api) {
      const firstRowNode = gridRef.current.api.getDisplayedRowAtIndex(0);
      if (firstRowNode) {
        gridRef.current.api.startEditingCell({
          rowIndex: firstRowNode.rowIndex,
          colKey: "columnName",
        });
      }
    }
  }, []);

  const addNewRow = () => {
    const previousRowFilled =
      rowData.length === 0 ||
      Object.values(rowData[0]).some(
        (value) => value !== undefined && value !== ""
      );
    if (previousRowFilled) {
      const newRowData = [{}, ...rowData];
      gridRef.current.api.applyTransaction({ add: [{}, ...rowData] });
      setRowData(newRowData);
      setTimeout(() => {
        focusFirstCell();
        gridRef.current.api.forEachNode((node) => {
          if (node.rowIndex === ZERO) node.setSelected(true);
        });
      }, 100);
    } else {
      const tableContainer = document.querySelector(".container");
      tableContainer.classList.add("shake");
      setTimeout(() => {
        tableContainer.classList.remove("shake");
      }, 1000);
    }
  };

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  const onCellValueChanged = (params) => {
    const rowIndex = params.node.rowIndex;
    const { colDef, newValue } = params;
    let { data } = params;
    let updatedRowsData = [ ...rowData]
    if (colDef.field === DATA_TYPE) {
      handleDataTypeChange({ value: newValue });
    }
    if (colDef.field === IS_PRIMARY) {
      if (newValue) {
        updatedRowsData = updatedRowsData.map((data) => {
          if (data.isPrimary) {
            return {
              ...data,
              isPrimary: false,
            };
          }
          return data;
        });
        data.isRequired = newValue;
        delete data.defaultValue
      }
    }
    updatedRowsData[rowIndex] = { ...data };
    setRowData(updatedRowsData);
  };

  return (
    <div className="container mx-auto overflow-y-scroll no-scrollbar max-h-72">
      <div className="flex mb-1 ml-auto bg-gray-900 text-white items-center rounded-md p-1 top-0 sticky z-10">
        <div className="flex-1 text-base">
          <span className="mr-2">Columns</span>
        </div>
        <button
          className="cursor-pointer text-xl font-bold hover:animate-pulse hover:text-blue-400"
          onClick={addNewRow}
          type="button"
        >
          +
        </button>
      </div>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={rowData}
        onGridReady={onGridReady}
        className="ag-theme-alpine"
        animateRows="true"
        domLayout="autoHeight"
        gridOptions={{
          defaultColDef: { resizable: true },
        }}
        ref={gridRef}
        onCellValueChanged={onCellValueChanged}
        rowDragManaged="true"
        getRowClass={(params) =>
          params.node.isSelected() ? "bg-blue-200" : ""
        }
        getRowStyle={(params) => ({
          pointerEvents: "auto",
          opacity: 1,
        })}
        rowDragEntireRow="true"
        onGridSizeChanged={onGridReady}
      ></AgGridReact>
    </div>
  );
};

const CreateCollectionModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const predefinedCollections = useSelector(selectPredefinedCollections);
  const dataModelId = useSelector(selectSelectedDataModelId);
  const selectedCollection = useSelector(selectCollection);

  const [currentStep, setCurrentStep] = useState(FIRST);
  const [selectedCollectionTemplate, setSelectedCollectionTemplate] = useState({
    name: EMPTY_STRING,
    properties: [],
  });
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const [rowData, setRowData] = useState([]);

  const [initialValues, setInitialValues] = useState({
    name: EMPTY_STRING,
    primaryKey: EMPTY_STRING,
  });

  const validationSchema = Yup.object({
    name: Yup.string()
      .test(
        CHECK_PROPERTY_NAME,
        COLLECTION_REQUIRED,
        (val) => currentStep === FIRST || val || selectedCollection
      )
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAIN)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .matches(
        SPECIAL_CHARACTERS_WHITESPACE_IN_BETWEEN,
        NAME_SHOULD_NOT_CONTAIN_WHITESPACE_IN_BETWEEN
      )
      .required("Please Enter Collection name."),
  });

  const collectionsTemplate = predefinedCollections?.filter(
    (each) => each.name !== "Start from Scratch"
  );

  const tabs = [
    {
      label: "General",
      key: "template",
      handleTabClick: (step) => {
        if (step !== currentStep) setCurrentStep(step);
      },
      step: FIRST,
    },
    {
      label: "Columns",
      key: "properties",
      handleTabClick: (step) => {
        if (step !== currentStep) setCurrentStep(step);
      },
      step: SECOND,
    },
  ];

  useEffect(() => {
    if (selectedCollection) {
      setInitialValues((prev) => ({ ...prev, ...selectedCollection }));
      setFilteredProperties(selectedCollection.schema.properties);
      setSelectedCollectionTemplate({
        name: selectedCollection.name,
        properties: selectedCollection.schema.properties,
      });
      setCurrentStep(SECOND);
    }
  }, [selectedCollection]);

  useEffect(() => {
    const { name, properties } = selectedCollectionTemplate;
    const primaryKeyField = properties.find((each) => each.meta.isKey);
    if (name && !selectedCollection && primaryKeyField) {
      setFilteredProperties(properties);
      setInitialValues((prev) => ({
        ...prev,
        name,
        primaryKey: primaryKeyField.schema.key,
      }));
    }
  }, [selectedCollectionTemplate, selectedCollection]);

  const handleCollectionSelection = useCallback(
    (selectedCollection, values) => {
      if (selectedCollection.properties) {
        setSelectedCollectionTemplate({
          name: selectedCollection.name,
          properties: selectedCollection.properties,
        });
        const formattedRows = selectedCollection.properties.map(
          (formattedObject) => {
            const { name, meta, schema } = formattedObject;
            const isPrimary = meta.isKey;
            const defaultValue = schema.defaultValue || EMPTY_STRING;
            const columnName = name;
            const dataType = meta.description;
            const originalObject = {
              columnName,
              isPrimary,
              dataType,
              defaultValue,
              ...meta,
            };

            return originalObject;
          }
        );

        setRowData(formattedRows);
      } else
        setSelectedCollectionTemplate({
          name: values.name,
          properties: [],
        });
    },

    []
  );

  const getFormattedProperties = (inputArray) => {
    return inputArray.map((item) => {
      const {
        columnName,
        isPrimary,
        dataType,
        isRequired,
        referenceTableId,
      } = item;

      const name = columnName;
      const { dataType: inputDataType, interface: inputInterface } =
        getInterfaceAndDatatype(dataType);
      const meta = {
        description: dataType,
        hidden: isPrimary || false,
        interface: inputInterface,
        readOnly: isPrimary || false,
        isRequired: !!isRequired,
        isKey: isPrimary || false,
      };

      let key = columnName.replace(/\s/g, "_");
      key = key.charAt(0).toLowerCase() + key.slice(1);

      let defaultValue = item.defaultValue;
      if (inputInterface === DATE_TIME) 
        defaultValue = new Date().toISOString();
      else if (inputInterface === IMAGE)
        meta["selectedChoices"] = SELECTED_CHOICES;

      let schema = {
        key,
        defaultValue: defaultValue || EMPTY_STRING,
        type: inputDataType,
      };

      if (dataType === "One-to-Many") {
        schema.referenceTableId = referenceTableId;
      }
      const formattedObject = {
        name,
        meta,
        schema,
      };

      return formattedObject;
    });
  };

  useEffect(() => {
    if (showError) {
      const hasIncompleteData = rowData.some(
        (data) => !data.columnName || !data.dataType
      );
      const noPrimaryTrue = !rowData.some((data) => data.isPrimary === true);
      if (!selectedCollection) setShowError(hasIncompleteData || noPrimaryTrue);
      else setShowError(hasIncompleteData);
    }
  }, [rowData, showError, selectedCollection]);

  const handleOnSubmit = useCallback(
    (values) => {
      if (currentStep === FIRST) {
        setCurrentStep(SECOND);
        return;
      }
      const hasIncompleteData = rowData.some(
        (data) => !data.columnName || !data.dataType
      );
      hasIncompleteData && setErrorMessage(FieldsErrorMessage);
      const noPrimaryTrue = !rowData.some((data) => data.isPrimary === true);
      noPrimaryTrue &&
        !selectedCollection &&
        setErrorMessage(PrimaryKeyMessage);

      if (hasIncompleteData || (noPrimaryTrue && !selectedCollection)) {
        setShowError(true);
        return;
      }

      setShowError(false);
      let updatedCollection = {};
      const insertedRecords = rowData;
      const formattedProperties = getFormattedProperties(insertedRecords);

      if (!selectedCollection) {
        const primaryKeyField = formattedProperties.find(
          (each) => each.meta.isKey
        );

        updatedCollection = {
          ...values,
          dataModelId: dataModelId,
          primaryKey: primaryKeyField.schema.key,
          schema: {
            properties: formattedProperties,
          },
        };
      } else {
        updatedCollection = {
          dataModelId: dataModelId,
          id: selectedCollection.id,
          data: {
            changeSet: {
              ...selectedCollection,
              schema: {
                properties: formattedProperties,
              },
            },
            operation: "create",
          },
        };
      }

      dispatch(
        selectedCollection
          ? updateCollection(updatedCollection)
          : createCollection(updatedCollection)
      )
        .then((res) => {
          if (res.payload) {
            dispatch(setSelectedModal());
            dispatch(setSelectedDrawer());
            navigate(
              `/data-models/${dataModelId}/collections/${res.payload.id}/details`,
              { replace: selectedCollection }
            );
          }
        })
        .catch((error) => {
          console.error("Error creating/updating collection:", error);
        });
    },
    [
      selectedCollection,
      dataModelId,
      dispatch,
      navigate,
      currentStep,
      rowData,
    ]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({ values, handleSubmit, touched, errors }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            {!selectedCollection && (
              <button
                className="btn btn-primary-light mr-1"
                type="button"
                onClick={() => {
                  if (currentStep === FIRST) {
                    dispatch(setSelectedModal());
                    setSelectedCollectionTemplate({
                      name: EMPTY_STRING,
                      properties: EMPTY_ARRAY,
                    });
                  } else setCurrentStep(FIRST);
                }}
              >
                {currentStep === FIRST ? "Cancel" : "Previous"}
              </button>
            )}
            <button
              className="btn btn btn-primary mr-1 disabled:cursor-not-allowed"
              type="Submit"
              disabled={
                currentStep !== FIRST &&
                (rowData.some((data) => isEmpty(data)) || isEmpty(rowData))
              }
            >
              <Spinner
                name={
                  selectedCollection ? UPDATE_COLLECTION : CREATE_COLLECTION
                }
                persistSize
              >
                {currentStep === FIRST ? "Next" : "Finish"}
              </Spinner>
            </button>
          </div>
        );

        return (
          <Form autoComplete="off" onSubmit={handleSubmit}>
            <Modal
              title="Create Collection"
              size={XL}
              footer={footer}
              handleClose={() => dispatch(setSelectedModal())}
            >
              <div className="flex rounded-sm bg-gray-900">
                <div className="flex">
                  {tabs.map(({ step, key, label, handleTabClick }) => (
                    <button
                      type="button"
                      className={classNames(
                        "text-sm hover:rounded-sm hover:bg-gray-600 py-2 px-4 text-gray-200 cursor-pointer disabled:cursor-not-allowed",
                        { "bg-gray-600 rounded-sm": currentStep === step }
                      )}
                      key={key}
                      onClick={() => handleTabClick(step)}
                      disabled={key !== "template" && !(selectedCollectionTemplate?.name.length >= 3)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {currentStep === FIRST && (
                <div className="p-4">
                  <div className="mb-2">
                    <div className="form-group">
                      <label className="text-sm font-medium text-gray-50 mb-1 block">
                        Collection Name
                      </label>
                      <Field
                        name="name"
                        type="text"
                        className="form-control border-2"
                        placeholder="Collection Name"
                        values={values.name}
                        disabled={selectedCollectionTemplate?.name}
                      />
                      {touched.name && errors.name && (
                        <div className="text-sm font-medium text-red-500">
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-base text-gray-200 m-3 text-center">
                    OR
                  </div>
                  <div className="text-base text-gray-200 m-3 text-center">
                    Select predefined template as collection
                  </div>

                  <div className="grid grid-cols-12 gap-6 mt-10">
                    {collectionsTemplate.map((collection) => (
                      <div
                        key={collection.name}
                        className={`col-span-3 md:col-span-2 ${
                          selectedCollectionTemplate ? "ring-indigo-700" : ""
                        }`}
                      >
                        <div
                          onClick={() =>
                            handleCollectionSelection(collection, values)
                          }
                          className={classNames(
                            "w-full text-center bg-gray-900 rounded-lg hover:ring-2 hover:ring-indigo-700 hover:shadow-md hover:shadow-indigo-700 hover:bg-gray-800 cursor-pointer relative",
                            {
                              " border border-indigo-700 shadow-md shadow-indigo-700":
                                selectedCollectionTemplate?.name ===
                                collection.name,
                            }
                          )}
                        >
                          {selectedCollectionTemplate?.name ===
                            collection.name && (
                            <div className="absolute top-0 right-0 m-2 cursor-pointer text-xs hover:rounded-full hover:bg-gray-900">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCollectionTemplate({
                                    name: "",
                                    properties: [],
                                  });
                                  setInitialValues({
                                    name: EMPTY_STRING,
                                    primaryKey: EMPTY_STRING,
                                  });
                                  setRowData(EMPTY_ARRAY);
                                  setFilteredProperties(EMPTY_ARRAY);
                                }}
                                className="text-gray-300"
                              >
                                <img
                                  src={crossIcon}
                                  className="h-3 w-3"
                                  alt="cross-icon"
                                ></img>
                              </button>
                            </div>
                          )}
                          <div className="icon p-2">
                            <img
                              src={`data:image/svg+xml,${collection.icon}`}
                              alt="template"
                              className="mx-auto"
                            />
                          </div>
                          <div className="pb-2 px-4">
                            <span className="text-sm font-medium text-gray-300">
                              {collection.name}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {currentStep === SECOND && (
                <div className="p-4">
                  <div className="flex justify-between mb-2 gap-x-3">
                    <div
                      className={classNames({
                        "w-1/2": selectedCollection,
                        "w-full": !selectedCollection,
                      })}
                    >
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-50 flex">
                          Collection Name
                        </label>
                        <Field
                          type="text"
                          value={values.name}
                          name="name"
                          className="form-control border-2"
                          placeholder="Collection Name"
                          disabled={true}
                        />
                      </div>
                    </div>
                    {selectedCollection && (
                      <div className="w-1/2">
                        <div className="form-group">
                          <label className="text-sm font-medium text-gray-50 flex">
                            Primary Key
                          </label>
                          <Field
                            name="primaryKey"
                            type="text"
                            className="form-control border-2"
                            placeholder="Primary Key"
                            values={values.primaryKey}
                            disabled={true}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <TableComponent
                    rowData={rowData}
                    setRowData={setRowData}
                    data={filteredProperties}
                    selectedCollectionTemplate = {selectedCollectionTemplate}
                  ></TableComponent>
                  {showError && (
                    <div className="text-sm font-medium text-red-500 px-4">
                      {errorMessage}
                    </div>
                  )}
                </div>
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateCollectionModal;
